/**
 * Paso Audiencia del asistente, pestaña «Público de la base» (SPEC-BD D8): elegir un público armado
 * con los contactos que se cargaron con su consentimiento (la pestaña «Con consentimiento» de Público).
 *
 * La lista la trae el asistente (la necesita para decidir con qué pestaña arrancar); acá se muestra y,
 * al elegir uno, se pide `GET /base/publicos/:id`, que recalcula el conteo en el momento: la cifra de la
 * lista es la última guardada y puede tener días. Mientras cuenta, la cifra anterior queda en gris.
 *
 * La cifra guardada se dice en pasado («podían recibir hace 4 h») y, en cuanto llega la recalculada, la
 * tarjeta de ese público dice la misma que el panel. En la recorrida de QA, «Clientes de la recorrida» decía
 * «48 pueden recibir (calculado hace 4 h)» y el chequeo de Confirmar, 0: en esas 4 h había salido una
 * campaña a esos 48 (46 quedaron por fatiga, uno pidió la baja y uno resultó sin WhatsApp). La regla era la
 * misma (`armarGates` + `decidirElegibilidad`, la del chequeo); lo viejo era la foto. Y la tarjeta seguía
 * diciendo 48 aunque el panel de abajo ya dijera 0: la lista que trae el asistente no se refresca.
 *
 * El conteo es el mismo cálculo que el chequeo de la campaña pero sin plantilla ni variables: el número
 * final lo da el chequeo del paso Variables.
 *
 * Con el filtro «Por compras» (28/10/2026, `compras`), el panel cuenta por `GET /audiencias/compras/base/:id`: los que
 * saca el filtro van primero entre los que quedan afuera, y el bloque (`porCompras`) dice cuántos quedan. La tarjeta
 * del público sigue con su cifra (la del público entero, sin el filtro de esta campaña). Con exclusiones (`excluir`,
 * 28/10/2026), por el mismo conteo: sus motivos van después del filtro, y los bloques reciben cuántos saca cada cosa.
 * Con listas que valen para todas las campañas (`listasSiempre`, 29/10/2026), también: el backend las descuenta solas, y
 * el número del panel es el de la campaña, no el del público entero.
 *
 * El número del panel lo pasa a «Tu campaña» (`onContado`, 29/10/2026): muestra lo elegido con la misma cuenta.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Upload } from "lucide-react";
import type { ConteoPublico, PublicoDetalle, PublicoResumen } from "../types.base";
import { ORDEN_GATES, type ExcluirAudiencia, type FiltroCompras, type ResumenCompras, type ResumenExclusiones } from "../types";
import { baseApi } from "../baseApi";
import { oficialApi } from "../api";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { MOTIVO_EXCLUSION } from "../logic/textos";
import { num, plural } from "../../campana-nueva/utils/format";
import { rutaSubirExcel } from "./AudienciaExcel";

const RB = "/activa/oficial/base";

/** «hace un momento» · «hace 5 min» · «hace 3 h» · «hace 2 días». Sin fecha legible, "". */
export function haceCuanto(iso: string | null | undefined, ahora: Date = new Date()): string {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(t)) return "";
  const min = Math.floor(Math.max(0, ahora.getTime() - t) / 60_000);
  if (min < 1) return "hace un momento";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} ${plural(d, "día", "días")}`;
}

type ConteoTarjeta = NonNullable<PublicoResumen["conteo"]>;

/**
 * Lo que dice la tarjeta de un público. Lo guardado (`enVivo: false`) es de la última vez que alguien lo abrió
 * y va en pasado: «60 contactos · 48 podían recibir hace 4 h». Lo que se recalculó en esta pantalla, en presente.
 * Un «pueden recibir» de hace horas se leía como de ahora, y Confirmar después decía otra cosa (ver arriba).
 */
export function textoConteoTarjeta(conteo: ConteoTarjeta | null | undefined, o: { enVivo: boolean; ahora?: Date }): string {
  if (!conteo) return "Todavía sin contar";
  const contactos = `${num(conteo.total)} ${plural(conteo.total, "contacto", "contactos")}`;
  const n = conteo.puedenRecibir;
  if (o.enVivo) return `${contactos} · ${num(n)} ${plural(n, "puede", "pueden")} recibir (recién contado)`;
  return `${contactos} · ${num(n)} ${plural(n, "podía", "podían")} recibir ${haceCuanto(conteo.at, o.ahora) || "la última vez que se contó"}`;
}

const TIPO_PUBLICO: Record<PublicoResumen["tipo"], { label: string; cls: string }> = {
  dinamico: { label: "Dinámico", cls: "bg-sky-50 text-sky-700" },
  estatico: { label: "Fijo", cls: "bg-slate-100 text-slate-600" },
};

interface Props {
  publicos: PublicoResumen[] | null;
  error: string;
  onReintentar: () => void;
  publicoId: string;
  onElegir: (p: PublicoResumen) => void;
  /** Ruta del asistente, para volver después de «Crear un público». */
  volverA: string;
  /** el filtro «Por compras» con el que se cuenta (null = sin filtro) */
  compras?: FiltroCompras | null;
  /** las exclusiones con las que se cuenta (null = sin exclusiones) */
  excluir?: ExcluirAudiencia | null;
  /** la marca tiene listas que valen para todas las campañas: se cuenta como la campaña aunque no elija nada */
  listasSiempre?: boolean;
  /** los bloques «Por compras» y «Excluir», con lo que dijo el conteo de cada uno */
  porCompras?: (r: { resumen: ResumenCompras | null; exclusiones: ResumenExclusiones | null; contando: boolean }) => ReactNode;
  /** cuántos del elegido pueden recibir, como lo dice el panel, para «Tu campaña» (null mientras cuenta, si no pudo o sin elegir) */
  onContado?: (pueden: number | null) => void;
}

export function AudienciaBase({ publicos, error, onReintentar, publicoId, onElegir, volverA, compras = null, excluir = null, listasSiempre = false, porCompras, onContado }: Props) {
  const nombreGrupo = useId();
  const [detalle, setDetalle] = useState<PublicoDetalle | null>(null);
  const [contando, setContando] = useState(false);
  const [errorConteo, setErrorConteo] = useState("");
  const [intento, setIntento] = useState(0);
  const [baseVacia, setBaseVacia] = useState(false);
  /** Lo que se recalculó en esta pantalla, por público: la lista del asistente trae lo guardado y no se refresca. */
  const [frescos, setFrescos] = useState<Record<string, ConteoTarjeta>>({});
  const seq = useRef(0);
  /** El conteo con el filtro «Por compras» (sólo con uno): el panel dice éste en lugar del del público entero. */
  const [conteoCompras, setConteoCompras] = useState<(ConteoPublico & { publicoId: string }) | null>(null);
  const [contandoCompras, setContandoCompras] = useState(false);
  const [errorCompras, setErrorCompras] = useState("");
  const seqCompras = useRef(0);
  const claveCompras = compras ? JSON.stringify(compras) : "";
  const claveExcluir = excluir ? JSON.stringify(excluir) : "";

  // Conteo en vivo del público elegido. Una respuesta vieja (se eligió otro mientras contaba) se descarta.
  useEffect(() => {
    if (!publicoId) { setDetalle(null); setErrorConteo(""); return; }
    const mio = ++seq.current;
    setContando(true);
    setErrorConteo("");
    (async () => {
      try {
        const l = leerRespuesta<PublicoDetalle>(await baseApi.publico(publicoId));
        if (mio !== seq.current) return;
        if (!l.ok || !l.data) { setErrorConteo(l.ok ? "El servidor no devolvió el público." : l.error || textoError(l)); return; }
        const d = l.data;
        setDetalle(d);
        const c = d.conteoActual;
        if (c) setFrescos((f) => ({ ...f, [d.id]: { total: c.total, puedenRecibir: c.puedenRecibir, at: c.calculadoAt } }));
      } catch (e) {
        if (mio !== seq.current) return;
        setErrorConteo(textoError(leerError(e)));
      } finally {
        if (mio === seq.current) setContando(false);
      }
    })();
  }, [publicoId, intento]);

  // Con el filtro «Por compras» o con exclusiones, el conteo del público elegido con ellos (aparte del detalle, que trae
  // las condiciones y la cifra de la tarjeta). Una respuesta vieja se descarta.
  useEffect(() => {
    const mio = ++seqCompras.current;
    if (!publicoId || (!claveCompras && !claveExcluir && !listasSiempre)) { setConteoCompras(null); setErrorCompras(""); setContandoCompras(false); return; }
    setContandoCompras(true);
    setErrorCompras("");
    const t = setTimeout(() => {
      void (async () => {
        try {
          const conCompras = claveCompras ? (JSON.parse(claveCompras) as FiltroCompras) : null;
          const conExcluir = claveExcluir ? (JSON.parse(claveExcluir) as ExcluirAudiencia) : null;
          const l = leerRespuesta<ConteoPublico>(await oficialApi.audienciaBaseConCompras(publicoId, conCompras, conExcluir));
          if (mio !== seqCompras.current) return;
          if (!l.ok || !l.data) { setErrorCompras(l.ok ? "El servidor no devolvió el conteo." : l.error || textoError(l)); return; }
          setConteoCompras({ ...l.data, publicoId });
        } catch (e) {
          if (mio === seqCompras.current) setErrorCompras(textoError(leerError(e)));
        } finally {
          if (mio === seqCompras.current) setContandoCompras(false);
        }
      })();
    }, 250);
    return () => clearTimeout(t);
  }, [publicoId, claveCompras, claveExcluir, listasSiempre, intento]);

  // con el filtro, con exclusiones o con listas que valen siempre, el conteo es el de `/audiencias/compras/base/:id`
  const conCompras = !!claveCompras || !!claveExcluir || listasSiempre;
  // con el filtro «Por compras», el panel dice el conteo con el filtro; sin él, el del público
  const conteo = conCompras
    ? (conteoCompras && conteoCompras.publicoId === publicoId ? conteoCompras : null)
    : detalle && detalle.id === publicoId ? detalle.conteoActual : null;
  const contandoPanel = contando || (conCompras && contandoCompras);
  const errorPanel = errorConteo || (conCompras ? errorCompras : "");
  // el número del panel, para «Tu campaña»
  const pueden = publicoId && conteo && !contandoPanel && !errorPanel ? conteo.puedenRecibir : null;
  useEffect(() => {
    onContado?.(pueden);
  }, [onContado, pueden]);

  // Sin públicos: si tampoco hay contactos, además de «Crear un público» se ofrece «Subir un Excel».
  const sinPublicos = !!publicos && publicos.length === 0;
  useEffect(() => {
    if (!sinPublicos) return;
    let vivo = true;
    (async () => {
      try {
        const l = leerRespuesta<{ contactos: number }>(await baseApi.resumen());
        if (vivo && l.ok && l.data) setBaseVacia(!(Number(l.data.contactos) > 0));
      } catch { /* sin el resumen se ofrece sólo «Crear un público»: el editor de públicos también lleva a subir */ }
    })();
    return () => { vivo = false; };
  }, [sinPublicos]);

  const crear = `${RB}/publicos/nuevo?volverA=${encodeURIComponent(volverA)}`;
  const enlaceCrear = (
    <Link to={crear} className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
      <Plus size={14} /> Crear un público
    </Link>
  );

  if (error) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
        <span>No pudimos traer los públicos. {error}</span>
        <button type="button" onClick={onReintentar} className="font-semibold underline">Reintentar</button>
      </div>
    );
  }
  if (!publicos) {
    return (
      <div className="space-y-2" aria-busy="true" aria-label="Cargando los públicos">
        {[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-slate-100 animate-pulse" />)}
      </div>
    );
  }
  if (!publicos.length) {
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-600">
        Todavía no hay públicos. Armá uno con los contactos que cargaste con su consentimiento.
        <div className="mt-3 flex justify-center gap-4 flex-wrap">
          {enlaceCrear}
          {baseVacia && (
            <Link to={rutaSubirExcel(volverA)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
              <Upload size={14} /> Subir un Excel
            </Link>
          )}
        </div>
      </div>
    );
  }

  const elegido = publicos.find((p) => p.id === publicoId);
  const excluidos = conteo ? ORDEN_GATES.filter((m) => (conteo.excluidos?.[m] ?? 0) > 0) : [];
  // mientras recuenta, en gris la última cifra que se vio (la de esta pantalla si la hay; si no, la guardada)
  const previo = elegido ? frescos[elegido.id] ?? elegido.conteo : null;
  return (
    <div className="space-y-4">
      <fieldset>
        <legend className="text-xs font-semibold text-slate-600 mb-2">Público</legend>
        <ul className="space-y-2">
          {publicos.map((p) => {
            const tipo = TIPO_PUBLICO[p.tipo] ?? TIPO_PUBLICO.dinamico;
            return (
              <li key={p.id}>
                <label className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 cursor-pointer ${publicoId === p.id ? "border-violet-400 bg-violet-50/60" : "border-slate-200 hover:border-slate-300"}`}>
                  <input type="radio" name={nombreGrupo} checked={publicoId === p.id} onChange={() => onElegir(p)} className="mt-1 accent-violet-600" aria-label={p.nombre} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">{p.nombre}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${tipo.cls}`}>{tipo.label}</span>
                    </span>
                    <span className="block text-xs text-slate-500">
                      {frescos[p.id] ? textoConteoTarjeta(frescos[p.id], { enVivo: true }) : textoConteoTarjeta(p.conteo, { enVivo: false })}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </fieldset>

      {porCompras?.({
        resumen: claveCompras && elegido && !contandoPanel ? conteo?.compras ?? null : null,
        exclusiones: conCompras && elegido && !contandoPanel ? conteo?.exclusiones ?? null : null,
        contando: !!elegido && contandoPanel,
      })}

      {elegido && (
        <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700 space-y-2" aria-live="polite">
          {errorPanel ? (
            <p className="flex items-center justify-between gap-3 text-red-700" role="alert">
              <span>No pudimos contar. {errorPanel}</span>
              <button type="button" onClick={() => setIntento((n) => n + 1)} className="font-semibold underline">Reintentar</button>
            </p>
          ) : conteo && !contandoPanel ? (
            <>
              <div className="flex items-start justify-between gap-3">
                <p>
                  <strong>{num(conteo.puedenRecibir)}</strong> de {num(conteo.total)} {plural(conteo.total, "contacto", "contactos")} pueden recibir ahora.
                </p>
                {/* La fatiga, las bajas y los límites de Meta cambian con las horas: si el asistente queda abierto, se vuelve a contar acá. */}
                <button type="button" onClick={() => setIntento((n) => n + 1)} className="shrink-0 text-xs font-semibold text-violet-700 hover:underline">
                  Recalcular
                </button>
              </div>
              {/* Por qué no entran los demás, con los mismos motivos que el chequeo: un «0 de 60» sin esto parecía un error. */}
              {excluidos.length > 0 && (
                <p className="text-xs text-slate-600">
                  Quedan afuera: {excluidos.map((m) => `${MOTIVO_EXCLUSION[m]?.titulo ?? m} (${num(conteo.excluidos[m] ?? 0)})`).join(" · ")}.
                </p>
              )}
            </>
          ) : (
            <p className="flex items-center gap-2 text-slate-400">
              {previo && <span>{num(previo.puedenRecibir)} de {num(previo.total)}</span>}
              <Loader2 size={13} className="animate-spin" /> Contando…
            </p>
          )}
          {(detalle && detalle.id === publicoId ? detalle.condiciones : elegido.condiciones).length > 0 && (
            <ul className="list-disc pl-5 text-xs text-slate-500">
              {(detalle && detalle.id === publicoId ? detalle.condiciones : elegido.condiciones).map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          )}
          {conteo?.supuestos && <p className="text-xs text-slate-500">{conteo.supuestos}</p>}
        </div>
      )}

      <div>{enlaceCrear}</div>
    </div>
  );
}

export default AudienciaBase;
